import React, { useRef, useEffect, useCallback } from 'react';
import type { Landmark, CaptureSequence, CaptureSettings } from './types';
import { drawAP, drawLat, drawIdealGrid, drawViewportOrientationLabels } from './lib/drawing';
import { getTransformCSS } from './lib/image-transformation';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import { maskLowVisibilityLandmarks, scaleLandmarks } from './lib/realtime-landmark-mask';

interface RealtimeOverlayCanvasProps {
  isActive: boolean; // Camera active AND overlay enabled
  landmarks: Landmark[] | null;
  currentView: CaptureSequence | null;
  captureSettings: CaptureSettings;
  videoElement: HTMLVideoElement | null;
}

const RealtimeOverlayCanvas: React.FC<RealtimeOverlayCanvasProps> = ({
  isActive,
  landmarks,
  currentView,
  captureSettings,
  videoElement,
}) => {
  const { laymanMode } = usePostureMode();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Calculate displayed video dimensions accounting for object-contain
  const getDisplayedVideoDimensions = useCallback(() => {
    if (!videoElement) {
      return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
    }

    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;
    
    if (videoWidth === 0 || videoHeight === 0) {
      return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
    }
    
    // Get the parent container to calculate offsets
    const parent = videoElement.parentElement;
    if (!parent) {
      return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
    }
    
    const parentRect = parent.getBoundingClientRect();
    const containerAspect = parentRect.width / parentRect.height;
    const videoAspect = videoWidth / videoHeight;
    
    let displayedWidth: number;
    let displayedHeight: number;
    let offsetX = 0;
    let offsetY = 0;
    
    if (videoAspect > containerAspect) {
      // Video is wider - fit to width
      displayedWidth = parentRect.width;
      displayedHeight = parentRect.width / videoAspect;
      offsetY = (parentRect.height - displayedHeight) / 2;
    } else {
      // Video is taller - fit to height
      displayedHeight = parentRect.height;
      displayedWidth = parentRect.height * videoAspect;
      offsetX = (parentRect.width - displayedWidth) / 2;
    }
    
    return { width: displayedWidth, height: displayedHeight, offsetX, offsetY };
  }, [videoElement]);

  // Transform landmarks from video coordinates to canvas coordinates (displayed dimensions, no offset)
  const transformLandmarks = useCallback((landmarks: Array<Landmark | null>): Array<Landmark | null> => {
    if (!videoElement || landmarks.length === 0) return landmarks;
    
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;
    
    if (videoWidth === 0 || videoHeight === 0) return landmarks;
    
    const { width: displayedWidth, height: displayedHeight } = getDisplayedVideoDimensions();
    
    if (displayedWidth === 0 || displayedHeight === 0) return landmarks;
    
    const scaleX = displayedWidth / videoWidth;
    const scaleY = displayedHeight / videoHeight;
    
    // Transform to canvas coordinates (offset is handled by canvas positioning)
    return scaleLandmarks(landmarks, scaleX, scaleY);
  }, [videoElement, getDisplayedVideoDimensions]);

  // Blank low-visibility landmarks in place (never remove them: drawers index by IDX)
  const filterLandmarks = useCallback((landmarks: Landmark[]): Array<Landmark | null> => {
    return maskLowVisibilityLandmarks(landmarks, captureSettings.visibilityThreshold);
  }, [captureSettings.visibilityThreshold]);

  // Drawing function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isActive || !currentView || !landmarks || landmarks.length === 0 || !videoElement) {
      return;
    }

    // Ensure video has valid dimensions
    if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get displayed dimensions
    const { width, height } = getDisplayedVideoDimensions();
    if (width === 0 || height === 0) {
      // Clear canvas if dimensions are invalid
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    // Set canvas size to match displayed video size
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Filter and transform landmarks
    const filtered = filterLandmarks(landmarks);
    if (!filtered.some(Boolean)) return;

    // Drawers skip null (blanked) points; the array keeps MediaPipe index alignment.
    const transformed = transformLandmarks(filtered) as Landmark[];
    
    // Validate transformed landmarks
    if (!transformed || transformed.length === 0) return;

    // Draw grid and plumb line
    if (captureSettings.gridType !== 'none' || captureSettings.showPlumbLine) {
      drawIdealGrid(ctx, currentView, transformed, {
        gridLineColor: captureSettings.gridLineColor,
        plumbLineColor: captureSettings.plumbLineColor,
        overlayLineWidth: captureSettings.overlayLineWidth,
        showPlumbLine: captureSettings.showPlumbLine,
        plumbLineStyle: captureSettings.plumbLineStyle,
        gridType: captureSettings.gridType,
      });
    }

    // Draw landmarks and skeleton
    if (currentView === 'Anterior' || currentView === 'Posterior') {
      drawAP(ctx, transformed, {
        landmarkSize: captureSettings.landmarkSize,
        landmarkFillColor: captureSettings.landmarkFillColor,
        landmarkStrokeColor: captureSettings.landmarkStrokeColor,
        skeletonLineWidth: captureSettings.skeletonLineWidth,
        skeletonLineColor: captureSettings.skeletonLineColor,
      });
    } else if (currentView === 'Left' || currentView === 'Right') {
      // No custom landmarks in real-time
      drawLat(ctx, transformed, currentView, {}, {
        landmarkSize: captureSettings.landmarkSize,
        landmarkFillColor: captureSettings.landmarkFillColor,
        landmarkStrokeColor: captureSettings.landmarkStrokeColor,
        skeletonLineWidth: captureSettings.skeletonLineWidth,
        skeletonLineColor: captureSettings.skeletonLineColor,
        customLandmarkColor: captureSettings.customLandmarkColor,
      });
    }

    drawViewportOrientationLabels(ctx, currentView, undefined, laymanMode);
  }, [
    isActive,
    currentView,
    landmarks,
    captureSettings,
    videoElement,
    getDisplayedVideoDimensions,
    filterLandmarks,
    transformLandmarks,
    laymanMode,
  ]);

  // Animation loop
  useEffect(() => {
    if (!isActive) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      // Clear canvas when inactive
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
      return;
    }

    const animate = () => {
      draw();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, [isActive, draw]);

  // Handle resize - dimensions are recalculated on each draw call
  // No need for separate resize handler as animation loop handles it

  if (!isActive) return null;

  const transformCSS = getTransformCSS(captureSettings);
  const { width, height, offsetX, offsetY } = getDisplayedVideoDimensions();

  // Don't render if dimensions are invalid
  if (width === 0 || height === 0) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none z-20"
    >
      <canvas
        ref={canvasRef}
        className="absolute pointer-events-none"
        style={{
          left: `${offsetX}px`,
          top: `${offsetY}px`,
          width: `${width}px`,
          height: `${height}px`,
          transform: transformCSS !== 'none' ? transformCSS : undefined,
          transformOrigin: 'center center',
        }}
      />
    </div>
  );
};

export default RealtimeOverlayCanvas;

